import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import api from '../../services/api';
import ExpenseClaimForm, { buildExpenseClaimPayload } from '../ApprovalCenter/ExpenseClaimForm';
import PrepaymentForm, { buildPrepaymentPayload } from '../ApprovalCenter/PrepaymentForm';
import GroupedFinanceFormSnapshotView from './GroupedFinanceFormSnapshotView';
import { prefillExpenseClaimForm, prefillPrepaymentForm } from '../../utils/processResubmitPrefill';

const EXPENSE_CLAIM_RESUBMIT_TYPES = new Set(['daily', 'travel', 'prepayment_append']);

function SnapshotBody({ processType, snapshotItems, formData }) {
  return (
    <GroupedFinanceFormSnapshotView
      items={snapshotItems}
      formData={formData}
      processType={processType}
    />
  );
}

export default function FinanceProcessResubmitPanel({
  processType,
  process,
  formData = {},
  snapshotItems = [],
  onFormChange
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const displayData = useMemo(() => ({ ...(process?.form_data || {}), ...formData }), [process?.form_data, formData]);

  const editLabel = processType === 'exp_prepayment' ? '编辑预付款表单' : '编辑报销表单';

  useEffect(() => {
    if (!editOpen) return;
    if (processType === 'exp_prepayment') {
      prefillPrepaymentForm(form, displayData);
    } else if (EXPENSE_CLAIM_RESUBMIT_TYPES.has(processType)) {
      prefillExpenseClaimForm(form, displayData);
    }
  }, [editOpen, form, displayData, processType]);

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      if (processType === 'exp_prepayment') {
        const payload = await buildPrepaymentPayload(form, true);
        const res = await api.post('/expense/exp-prepayment/preview', { payload, submit: true });
        if (!res.data?.success) throw new Error(res.data?.message || '保存失败');
        onFormChange?.(res.data.data.form_data, res.data.data.form_display);
      } else if (EXPENSE_CLAIM_RESUBMIT_TYPES.has(processType)) {
        const payload = await buildExpenseClaimPayload(form, true);
        if (processType === 'travel') payload.claim_type = 'travel';
        if (processType === 'prepayment_append') payload.claim_type = 'daily';
        const res = await api.post('/expense/claims/preview', { ...payload, submit: true });
        if (!res.data?.success) throw new Error(res.data?.message || '保存失败');
        onFormChange?.(res.data.data.form_data, res.data.data.form_display);
      }
      message.success('表单已更新，请确认后重新提交');
      setEditOpen(false);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.response?.data?.message || e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="流程已退回，请核对申请内容"
        description="如需修改字段，请先点击下方按钮编辑完整业务表单，保存后再点「重新提交」。"
        action={(
          <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => setEditOpen(true)}>
            {editLabel}
          </Button>
        )}
      />
      {snapshotItems.length > 0 && (
        <SnapshotBody
          processType={processType}
          snapshotItems={snapshotItems}
          formData={displayData}
        />
      )}
      <Modal
        title={editLabel}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleSaveEdit}
        confirmLoading={saving}
        okText="保存修改"
        cancelText="取消"
        width={960}
        destroyOnHidden
        styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
      >
        {processType === 'exp_prepayment' ? (
          <PrepaymentForm form={form} />
        ) : (
          <ExpenseClaimForm form={form} />
        )}
      </Modal>
    </>
  );
}
